import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Card, Checkbox, Intent, Tag } from '@blueprintjs/core';
import styled from 'styled-components';
import { capitalizeString, formatDate, parseDate } from 'utils/format';
import uniqBy from 'utils/uniqBy';
import objectFromEntries from 'utils/objectFromEntries';

const CardBody = styled.div`
  position: relative;
  flex-grow: 1;
`;

const EventList = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  overflow: auto;
`;

const CardFilter = styled.div`
  margin-bottom: 10px;
`;

const CardFooter = styled.div`
  margin-top: 10px;
  display: flex;
  justify-content: space-between;
`;

const StyledTag = styled(Tag)`
  margin-right: 5px;
`;

const TYPE_LABEL = {
  Birth: 'Births',
  Death: 'Deaths',
  Marriage: 'Marriages',
};

const EventListCard = props => {
  const [events, types] = useMemo(() => {
    if (!props.events) {
      return [null, null];
    }

    // remove duplicite events
    const type = {};
    const uniqEvents = uniqBy(props.events, event => {
      if (event.type) {
        type[event.type] = true;
      }
      return `${event[props.dateKey]}-${event[props.descriptionKey]}`;
    });

    return [uniqEvents, Object.keys(type)];
  }, [props.events, props.dateKey, props.descriptionKey]);

  const [selectedEvents, setSelectedEvents] = useState({});
  useEffect(() => {
    setSelectedEvents({});
  }, [events]);

  const numberOfSelectedEvents = useMemo(() => {
    return Object.entries(selectedEvents).filter(([, value]) => Boolean(value)).length;
  }, [selectedEvents]);

  const handleSelectAllEvents = select => {
    if (select) {
      setSelectedEvents(objectFromEntries(events.map(event => [event.key, true])));
    } else {
      setSelectedEvents({});
    }
  };

  const handleSelectedChange = key => {
    setSelectedEvents(value => ({
      ...value,
      [key]: value[key] ? undefined : true,
    }));
  };

  const handleAddEvents = () => {
    props.onAddEvents(events.filter(event => selectedEvents[event.key]));
    setSelectedEvents({});
  };

  const handleSelectType = type => {
    setSelectedEvents(value => ({
      ...value,
      ...objectFromEntries(
        events.filter(event => event.type === type).map(event => [event.key, true]),
      ),
    }));
  };

  if (!events) {
    return null;
  }

  if (events.length === 0) {
    return <>Sorry, we didn&apos;t find any events.</>;
  }

  return (
    <Card className="event-list-card mt-3">
      {props.showTypeFilter && types.length > 0 && (
        <CardFilter>
          {types.map(type => (
            <Button key={type} minimal onClick={() => handleSelectType(type)}>
              {TYPE_LABEL[type] ?? capitalizeString(type)}
            </Button>
          ))}
        </CardFilter>
      )}
      <CardBody>
        <EventList>
          {events.map(event => (
            <Checkbox
              key={event.key}
              checked={selectedEvents[event.key] ?? false}
              onChange={() => handleSelectedChange(event.key)}
            >
              <StyledTag minimal>
                {formatDate(parseDate(event[props.dateKey], props.dateFormat), props.dateMask)}
              </StyledTag>
              <span>{event[props.descriptionKey]}</span>
            </Checkbox>
          ))}
        </EventList>
      </CardBody>
      <CardFooter>
        <div>
          <Button minimal onClick={() => handleSelectAllEvents(true)}>
            Select All
          </Button>
          <Button minimal onClick={() => handleSelectAllEvents(false)}>
            Unselect All
          </Button>
        </div>
        <Button
          intent={Intent.SUCCESS}
          onClick={handleAddEvents}
          disabled={numberOfSelectedEvents === 0}
        >
          Add Selected Events{numberOfSelectedEvents > 0 && <> ({numberOfSelectedEvents})</>}
        </Button>
      </CardFooter>
    </Card>
  );
};

EventListCard.propTypes = {
  dateFormat: PropTypes.string,
  dateKey: PropTypes.string,
  descriptionKey: PropTypes.string,
  events: PropTypes.array,
  showTypeFilter: PropTypes.bool,
  onAddEvents: PropTypes.func,
  dateMask: PropTypes.string,
};

EventListCard.defaultProps = {
  dateFormat: 'YYYY-MM-DD',
  dateKey: 'date',
  descriptionKey: 'description',
  dateMask: 'MMM D',
  showTypeFilter: false,
};

export default EventListCard;
